import { useEffect, useState } from "react";
import { X, Check, ChevronRight, ChevronLeft, Clock } from "lucide-react";

const STEPS = [
  {
    page: "calculator",
    title: "Step 1 — Calculator",
    body: (
      <div className="space-y-2 text-sm text-zinc-600">
        <p>This is where everything starts. Tap <b className="text-orange-600">+ New Pallet Type</b> to add your pallet sizes and types — deck boards, stringers, nails, and anything else you cut.</p>
        <p>For each component you can enter <b>multiple lumber options</b> to cut it from, including <b>how many pieces you get per cant</b> (it shows the inches of waste), <b>pieces per skid</b>, and <b>board feet per piece</b>. Save it once and it's ready for future use.</p>
        <p>Then just tap the pallet type you want, enter the number of pallets, and hit <b className="text-orange-600">Calculate</b> — it shows total board feet, skids, lumber amounts, and exactly which lumber to use.</p>
        <img src="/tutorial/calculator.webp" alt="Calculator page with results" loading="lazy" className="w-full border border-zinc-200 my-1" data-testid="tutorial-img-calculator" />
        <p>When you like the numbers, tap <b className="text-blue-600">Add to Cut List</b> to send it to the Cut List.</p>
      </div>
    ),
  },
  {
    page: "cutlist",
    title: "Step 2 — Cut List",
    body: (
      <div className="space-y-2 text-sm text-zinc-600">
        <p>Everything you <b className="text-blue-600">Add to Cut List</b> lands on this page. Sort it, drag things around however you want, then start at the top and work your way down. This is similar to what the cut list looks like:</p>
        <div className="space-y-2 my-2" data-testid="tutorial-fake-cutlist">
          {/* Component 1 — expanded, in progress */}
          <div className="border-2 border-zinc-300 bg-white">
            <div className="flex items-center justify-between px-2.5 py-2 bg-zinc-50 border-b border-zinc-200">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="w-4 h-4 flex-shrink-0 border-2 border-zinc-400" />
                <ChevronRight className="w-3.5 h-3.5 text-zinc-400 rotate-90 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex-shrink-0 w-2 h-2">
                      <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75" />
                      <span className="relative block w-2 h-2 rounded-full bg-green-500" />
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-700" style={{ fontFamily: "Chivo, sans-serif" }}>Deck Boards</span>
                  </div>
                  <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400">Standard 48x40 GMA</p>
                </div>
              </div>
              <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600 flex-shrink-0"><Clock className="w-3 h-3" />12:41</span>
            </div>
            <div className="px-2.5 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Quantity</p>
                  <span className="text-xl font-bold text-orange-600 border border-dashed border-orange-300 px-2 py-0.5 inline-block" style={{ fontFamily: "Chivo, sans-serif" }}>462</span>
                </div>
                <span className="text-[10px] text-zinc-400">7/pallet &times; 66</span>
              </div>
              <div className="grid grid-cols-3 gap-x-3 gap-y-1.5 mt-2 pt-2 border-t border-zinc-100">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Size</p>
                  <span className="text-[11px] font-semibold text-zinc-800">5/8 &times; 3.5 &times; 40</span>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Board Ft</p>
                  <span className="text-[11px] font-semibold text-zinc-800">336.9</span>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Cut Date</p>
                  <span className="text-[11px] font-semibold text-zinc-800">6/12/2026</span>
                </div>
              </div>
              <div className="mt-2 p-2 border border-violet-200 bg-violet-50/50">
                <span className="text-[11px] font-bold text-zinc-700">18 — 3.5&quot; x 6&quot; x 40&quot; Poplar <span className="text-[9px] font-normal text-violet-500">(26 pcs/cant, 2&quot; waste)</span></span>
                <div className="mt-1.5 flex items-end gap-2.5 flex-wrap">
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Cants Used</p>
                    <span className="inline-block w-11 h-7 text-xs text-center leading-7 border border-violet-300 bg-violet-50 font-bold text-zinc-800">18</span>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Adjust</p>
                    <div className="flex items-center">
                      <span className="inline-block w-9 h-7 text-xs text-center leading-7 border border-zinc-200 bg-white text-zinc-400">#</span>
                      <span className="inline-block px-2 h-7 leading-7 text-sm font-bold border border-zinc-200 text-zinc-600">+</span>
                      <span className="inline-block px-2 h-7 leading-7 text-sm font-bold border border-zinc-200 text-zinc-600">−</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Species</p>
                    <span className="text-[11px] font-semibold text-zinc-800">Poplar</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Component 2 — collapsed, done */}
          <div className="border-2 border-green-300 bg-green-50/50">
            <div className="flex items-center justify-between px-2.5 py-2 bg-green-100/70">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="w-4 h-4 flex-shrink-0 border-2 bg-green-500 border-green-500 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></span>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-green-700 line-through" style={{ fontFamily: "Chivo, sans-serif" }}>Stringers 48&quot;</span>
                    <span className="text-xs font-bold text-green-600">198</span>
                  </div>
                  <p className="text-[9px] uppercase tracking-[0.15em] text-green-600/40 line-through">Standard 48x40 GMA</p>
                  <span className="text-[9px] font-semibold text-green-600/60">50 – 3.5&quot; x 6&quot; x 8&apos; Pine</span>
                </div>
              </div>
              <span className="flex items-center gap-1 text-[10px] font-semibold text-zinc-400 flex-shrink-0"><Clock className="w-3 h-3" />26:15</span>
            </div>
          </div>
        </div>
        <p>Each component has a <b>stopwatch</b> to time the cut, and a <b>checkbox</b> to cross it off when done. Change the quantity on any component and it <b>recalculates everything</b>. Tap a component to expand its full details — like the Deck Boards above.</p>
        <p>Got more than one cut-up line? You can create up to <b>15 separate cut lists</b> (like "Board Line") in <b>Settings → Cut Lists</b> — name each one, choose which list this device shows (or show all of them stacked), and when adding components you pick which list they go to.</p>
      </div>
    ),
  },
  {
    page: "cutlist",
    title: "Step 3 — Lumber in a Component",
    body: (
      <div className="space-y-2 text-sm text-zinc-600">
        <p>Inside each component you'll find its lumber recommendation, like this:</p>
        <div className="p-2 border border-violet-200 bg-violet-50/50 rounded-sm my-2" data-testid="tutorial-fake-lumber">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-zinc-700">10 — 3.5&quot; x 6&quot; x 8&apos; Poplar <span className="text-[10px] font-normal text-violet-500">(27 pcs/cant)</span></span>
          </div>
          <div className="mt-2 flex items-end gap-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Cants Used</p>
              <span className="inline-block w-14 h-8 text-sm text-center leading-8 border border-violet-300 bg-violet-50 font-bold text-zinc-800">10</span>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Adjust</p>
              <div className="flex items-center">
                <span className="inline-block w-12 h-8 text-sm text-center leading-8 border border-zinc-200 bg-white text-zinc-400">#</span>
                <span className="inline-block px-2.5 h-8 leading-8 text-base font-bold border border-zinc-200 text-zinc-600">+</span>
                <span className="inline-block px-2.5 h-8 leading-8 text-base font-bold border border-zinc-200 text-zinc-600">−</span>
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Waste</p>
              <span className="text-xs font-semibold text-zinc-800">0&quot;</span>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 mb-0.5">Species</p>
              <span className="text-xs font-semibold text-zinc-800">Poplar</span>
            </div>
          </div>
        </div>
        <p>Enter the amount of lumber being used by using the <b>Adjust buttons</b> — type a number and tap <b>+</b> to add it (or <b>−</b> to take it back off). The Cants Used box shows the running total.</p>
        <p className="p-2 bg-orange-50 border border-orange-200 text-zinc-700">
          <b>Important:</b> your <b>Lumber Inventory adjusts in real time</b> as you adjust the lumber amount. Tap <b>+</b> and that amount is <b>automatically subtracted from your inventory</b> the moment you tap it; tap <b>−</b> and the lumber goes <b>right back</b>. No checking off needed — the inventory always mirrors what you&apos;ve actually used.
        </p>
        <p>Don&apos;t want it touching your inventory? There&apos;s a <b>Lumber Auto-Deduction</b> switch in Settings — turn it <b>Off</b> and adjusting lumber won&apos;t subtract anything; turn it back <b>On</b> and it works like normal.</p>
        <p>We'll show you the Lumber Inventory next.</p>
      </div>
    ),
  },
  {
    page: "lumber",
    title: "Step 4 — Lumber Inventory",
    body: (
      <div className="space-y-2 text-sm text-zinc-600">
        <p>This page tracks all your lumber on hand. Use <b>Add Lumber</b> to add a new size, and each item's menu to <b>add or subtract</b> quantities as loads come in or get used.</p>
        <img src="/tutorial/lumber.webp" alt="Lumber Inventory page" loading="lazy" className="w-full border border-zinc-200 my-1" data-testid="tutorial-img-lumber" />
        <p>At the bottom of the page there&apos;s a <b>History</b> of every transaction — the <b>automatic ones come from adjusting lumber amounts in the Cut List</b>, and you can undo manual entries from the menu.</p>
        <p>Quantities can even go below zero (shown in red) — say a load of lumber comes in and doesn't get entered into the inventory for a while, and you use it up before it's logged. Once the load gets entered in, the count comes right back up correctly.</p>
      </div>
    ),
  },
  {
    page: "orders",
    title: "Step 5 — Orders",
    body: (
      <div className="space-y-2 text-sm text-zinc-600">
        <p>Add and monitor your customer orders here. Each order can hold <b>multiple pallet types</b>, load and delivery dates, file attachments, notes, and an <b>assembly timer</b> with crew size.</p>
        <img src="/tutorial/orders.webp" alt="Orders page" loading="lazy" className="w-full border border-zinc-200 my-1" data-testid="tutorial-img-orders" />
        <p>Use the <b>Calendar</b> button to see orders by date, check off pallet types as they're built, and mark orders finished when they're ready for pickup. Completed orders move to Order History in settings.</p>
      </div>
    ),
  },
  {
    page: null,
    title: "Step 6 — Settings & You're All Set!",
    body: (
      <div className="space-y-2 text-sm text-zinc-600">
        <p>Tap the <b>menu icon</b> (top right) to open Settings. Here's what's inside:</p>
        <ul className="list-disc pl-4 space-y-1 text-xs">
          <li><b>Account</b> — change your name, email, password, or <b>switch between accounts</b>.</li>
          <li><b>Cut Lists</b> — create up to 15 named cut lists and pick which one this device shows.</li>
          <li><b>Lumber Auto-Deduction</b> — turn automatic inventory subtracting on/off (applies to your whole account).</li>
          <li><b>Highlight Bar</b> — group same-thickness components with a colored bar (per device, off by default).</li>
          <li><b>Print & Order History</b> — past prints, plus completed orders grouped by month with search.</li>
          <li><b>Default Page</b> — which page the app opens on.</li>
          <li><b>Viewable Only</b> — lock pages to view-only on shop devices so nothing gets changed by accident.</li>
          <li><b>Subscription</b> — see your plan, subscribe, or cancel anytime.</li>
        </ul>
        <p className="font-semibold text-zinc-800">Thank you for choosing Pallet Cut Calculator! We built this to save you time in the shop — so you can spend less time figuring and more time cutting. If you ever want a refresher, this tutorial is always in Settings.</p>
      </div>
    ),
  },
];

export function AppTutorial({ active, onFinish, setActivePage, preLogin = false }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (active) {
      setStep(0);
      setActivePage && setActivePage("calculator");
      window.scrollTo(0, 0);
    }
  }, [active, setActivePage]);

  useEffect(() => {
    if (!active) return;
    const page = STEPS[step].page;
    if (page && setActivePage) setActivePage(page);
    window.scrollTo(0, 0);
  }, [step, active, setActivePage]);

  if (!active) return null;
  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="app-tutorial">
      <div className="absolute inset-0 bg-black/50" onClick={() => {}} />
      <div className="relative bg-white border border-zinc-300 shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto" data-testid={`tutorial-step-${step + 1}`}>
        <div className="sticky top-0 bg-zinc-900 text-white px-4 py-3 flex items-center justify-between">
          <h3 className="text-sm font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{s.title}</h3>
          <button onClick={onFinish} className="p-1 text-zinc-400 hover:text-white" data-testid="tutorial-exit-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">{s.body}</div>
        <div className="sticky bottom-0 bg-white border-t border-zinc-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <span key={i} className={`w-2 h-2 rounded-full ${i === step ? 'bg-orange-600' : 'bg-zinc-300'}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)}
                className="flex items-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-300 text-zinc-600 hover:bg-zinc-50"
                data-testid="tutorial-back-btn">
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            {last ? (
              <button onClick={onFinish}
                className="flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-orange-600 text-white hover:bg-orange-700"
                data-testid="tutorial-finish-btn">
                <Check className="w-3.5 h-3.5" /> {preLogin ? "Get Started" : "Finish"}
              </button>
            ) : (
              <button onClick={() => setStep(step + 1)}
                className="flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-orange-600 text-white hover:bg-orange-700"
                data-testid="tutorial-next-btn">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
